import { watch } from 'vue'
import { fetchProfile, setLanguage } from '~/composables/api/useUserApi'

/**
 * Keeps `users.language` in sync with the active UI locale for AUTHENTICATED
 * users:
 *  1. once on authed load — reconcile the stored value with the active locale:
 *     if they differ, persist the active locale, so existing accounts (and
 *     fresh signups) carry the language the user actually sees for
 *     backend-originated messages (emails, verification). NB: `users.language`
 *     is `NOT NULL DEFAULT 'en'`, so an emptiness check would never fire — a
 *     'uk' UI set via cookie/detection without an in-session switch would keep
 *     'en' on file and the withdrawal/verification emails would go out in
 *     English;
 *  2. on every locale switch — persist the newly chosen locale.
 *
 * No-op for guests. Owns the backend round-trip here so the base-layer
 * `useLocaleSync` (shared with the landing app) stays auth-agnostic.
 *
 * NOTE on the data source: the web's always-loaded current user comes from
 * `/auth-session` (useUserStore), which does NOT carry `language`. That field
 * lives only on `/user-profile`. So the "stored value" check reads the profile
 * via `fetchProfile` (mirroring useTelegramVerification's once-per-login fetch)
 * rather than a reactive `currentUser.language` ref — there isn't one. The
 * fetch runs at most once per authed session (guarded by `initRan`).
 *
 * MUST be called from a Vue component setup context (it lives in `app.vue`'s
 * `<script setup>`), NOT a Nuxt plugin: it goes through `useLocaleSync`, whose
 * `useI18n()` / `useRoute()` / `useSwitchLocalePath()` are setup-only and throw
 * "Must be called at the top of a `setup` function" outside a component.
 */
export function useUserLanguageSync(): void {
  const { currentLocale } = useLocaleSync()
  const { isAuthenticated } = useAuth()
  const { getAccessToken } = useSupabaseAuth()

  // Guards against re-running the init backfill — the auth watcher can fire
  // multiple times (optimistic paint → /auth-session reconcile) for a single
  // login, and we only want one profile read + at most one backfill POST.
  let initRan = false

  /**
   * On authed load: read the stored language; if it differs from the active UI
   * locale, POST the active locale so backend channels match what the user sees.
   * Best-effort — a failure here never blocks the UI.
   */
  async function runInit(): Promise<void> {
    if (initRan) return
    if (!isAuthenticated.value) return
    initRan = true

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) return

      const profile = await fetchProfile(accessToken)
      // Reconcile, don't just backfill-when-empty: `users.language` is
      // NOT NULL DEFAULT 'en', so an emptiness guard never fires. Push whenever
      // the stored value differs from the locale the user is actually using, so
      // backend-originated emails are sent in the same language as the UI.
      if (profile.language !== currentLocale.value) {
        await setLanguage(accessToken, currentLocale.value)
      }
    } catch (e) {
      if (import.meta.dev) console.error('[lang-sync] init failed', e)
    }
  }

  /**
   * On locale switch (while authed): persist the new locale. Independent of the
   * init backfill — this fires for every change, even after the stored value
   * is populated.
   */
  async function pushLocale(locale: string): Promise<void> {
    if (!isAuthenticated.value) return
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) return
      await setLanguage(accessToken, locale)
    } catch (e) {
      if (import.meta.dev) console.error('[lang-sync] switch failed', e)
    }
  }

  // Run the init backfill as soon as the user is (or becomes) authenticated.
  // `immediate` covers the already-authed-on-mount case; the watcher covers the
  // login-after-mount case. On logout we re-arm so the next login re-checks.
  watch(
    isAuthenticated,
    (authed) => {
      if (authed) {
        runInit()
      } else {
        initRan = false
      }
    },
    { immediate: true },
  )

  // Persist every locale change while authenticated.
  watch(currentLocale, (loc, prev) => {
    if (loc === prev) return
    pushLocale(loc)
  })
}
